/**
 * "Nastavi slušanje" (Q23): where the last session stopped, kept on this device only
 * (localStorage; no server sync). FixedAudioPlayer writes it; Početna reads it.
 */
import { z } from 'zod';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export const LISTENING_RESUME_STORAGE_KEY = 'listening-resume';
/** timeupdate fires about 4 times a second: write only after the position moved this far. */
export const RECORD_STEP_SECONDS = 5;

export interface ListeningResume {
  sessionId: string;
  /** Seconds from the start. */
  position: number;
  /** Seconds, from the audio metadata; 0 until it is known. */
  duration: number;
}

/** localStorage is outside our control: anything that is not this shape is dropped on load. */
const persistedSchema = z.object({
  resume: z
    .object({
      sessionId: z.string().min(1),
      position: z.number().nonnegative(),
      duration: z.number().nonnegative(),
    })
    .nullable(),
});

interface ListeningResumeState {
  resume: ListeningResume | null;
  /** Store the position; small steps of the same session are skipped unless `force` (pause). */
  record: (sessionId: string, position: number, duration: number, options?: { force?: boolean }) => void;
  /** Forget the entry if it belongs to this session (it ended). */
  clearSession: (sessionId: string) => void;
  clear: () => void;
}

function usableDuration(duration: number): number {
  return Number.isFinite(duration) && duration > 0 ? duration : 0;
}

export const useListeningResumeStore = create<ListeningResumeState>()(
  persist(
    (set, get) => ({
      resume: null,
      record: (sessionId, position, duration, options) => {
        if (!Number.isFinite(position) || position < 0) return;
        const current = get().resume;
        const known = usableDuration(duration);
        if (
          !options?.force &&
          current?.sessionId === sessionId &&
          Math.abs(position - current.position) < RECORD_STEP_SECONDS &&
          (current.duration > 0 || known === 0)
        ) {
          return;
        }
        set({ resume: { sessionId, position, duration: known || (current?.sessionId === sessionId ? current.duration : 0) } });
      },
      clearSession: (sessionId) => {
        if (get().resume?.sessionId === sessionId) set({ resume: null });
      },
      clear: () => set({ resume: null }),
    }),
    {
      name: LISTENING_RESUME_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ resume: state.resume }),
      merge: (persisted, current) => {
        const parsed = persistedSchema.safeParse(persisted);
        return { ...current, resume: parsed.success ? parsed.data.resume : null };
      },
    },
  ),
);

export const useListeningResume = () => useListeningResumeStore((state) => state.resume);
